import { FC, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Key, LoaderCircle } from 'lucide-react';
import { OrderStatusEnum } from '@libs/schemas/src/order';
import { Button } from '@/components/ui/button';
import { Order, OrderPayment, RouterOutput, trpc } from 'src/trpc';
import { formatTime } from 'src/util';
import { formatCalendarDate, formatDateTime, toValidDate } from '@libs/frontend-services';

type OrderNote = RouterOutput['admin']['note']['getNotes']['notes'][number];
type NoteCorrectionType = NonNullable<OrderNote['correctionType']>;

interface ActivityItem {
  date: Date;
  description: string;
  updatedBy?: string;
  adminMasqueradeUserId?: string;
  details?: string;
  type:
    | 'status_change'
    | 'scheduling'
    | 'non_card_payment'
    | 'refund'
    | 'delivery'
    | 'pickup'
    | 'system'
    | 'correction'
    | `${OrderPayment['type']}_payment`;
}

/**
 * Correction titles come from the note's structured `correctionType`, never from parsing the
 * human-readable `content` prose (which stays in the details line).
 */
const correctionDescriptions: Record<NoteCorrectionType, string> = {
  customer: 'Customer changed',
  address: 'Delivery address changed',
};

// Only these render for now. Corrections are included so the field-level change log lives in
// one newest-first timeline alongside payments and refunds, which is what dispute review needs.
const displayedTypes: ActivityItem['type'][] = [
  'card_payment',
  'link_payment',
  'us_bank_account_payment',
  'non-card_payment',
  'refund',
  'correction',
];

interface ActivityLogProps {
  order: Order;
}

// Linked to the notice heading/body so one `role="alert"` carries its own accessible name and
// description, matching the query-error pattern already approved on the marketing dashboard.
const CORRECTION_HISTORY_TITLE_ID = 'activity-log-correction-history-title';
const CORRECTION_HISTORY_BODY_ID = 'activity-log-correction-history-body';

/**
 * Whether the correction history behind this timeline is known to be complete.
 *
 * `loaded` is the only value that proves an absence of corrections. Corrections come from a
 * separate notes query, so they can still be pending or failed while payments and refunds are
 * already on screen - and an admin reviewing a dispute reads a silent, correction-free timeline
 * as evidence that no customer or address change ever happened. Anything short of a successful
 * load has to say so.
 *
 * `stale` is the case a plain `data ? 'loaded' : ...` check gets wrong. TanStack Query keeps the
 * last successful payload in `data` when a later invalidation or refetch fails, so `data` and
 * `isError` are true at the same time. Those cached entries are worth showing, but they are a
 * snapshot of an earlier load, not the current record - and they must never be the basis for
 * claiming the order has no corrections.
 */
type CorrectionHistoryState = 'loaded' | 'loading' | 'stale' | 'unavailable';

const formatDate = (date: Date | string | null | undefined): string => {
  return formatDateTime(date, "MMM d, yyyy 'at' h:mm a");
};

const formatTimeWindow = (start: number, end: number): string => {
  return formatTime(start, end);
};

export const ActivityLog: FC<ActivityLogProps> = ({ order }) => {
  const orderId = order._id ?? '';

  // The field-level change log is stored as order notes, so it needs its own query - it does
  // not ride along on the order document like payments and refunds do. That independence is
  // exactly why its pending and failed states have to be visible: the rest of the timeline
  // renders fine without it.
  const {
    data: noteData,
    isError: isNotesError,
    isFetching: isFetchingNotes,
    refetch: refetchNotes,
  } = trpc.admin.note.getNotes.useQuery(
    { entityId: orderId, origin: 'order' },
    { enabled: !!orderId },
  );

  const [notesRetryState, setNotesRetryState] = useState<'idle' | 'retrying' | 'failed'>('idle');
  const notesRetryInFlightRef = useRef(false);
  const isRetryingNotes = notesRetryState === 'retrying';

  // Failure is checked before `noteData`, not after. A cached payload left over from an earlier
  // successful load survives a failed refetch, so testing `noteData` first would relabel a known
  // failure as `loaded` and let a stale timeline pass for the current record.
  //
  // `enabled: false` (no order id) is not "no corrections" either - it is another way of not
  // knowing. Only a query that is not in error, with data, earns `loaded`.
  const correctionHistory: CorrectionHistoryState =
    isNotesError || !orderId
      ? noteData
        ? 'stale'
        : 'unavailable'
      : noteData
        ? 'loaded'
        : 'loading';

  // Both failure states keep the cached entries on screen and offer the same recovery.
  const isCorrectionHistoryFailed =
    correctionHistory === 'stale' || correctionHistory === 'unavailable';

  const handleRetryNotes = async (): Promise<void> => {
    // The button stays focusable via `aria-disabled` rather than `disabled`, so a second
    // activation can land while the first refetch is still open. Guard it.
    if (notesRetryInFlightRef.current || isFetchingNotes) return;
    notesRetryInFlightRef.current = true;
    setNotesRetryState('retrying');

    const result = await refetchNotes();

    notesRetryInFlightRef.current = false;
    setNotesRetryState(result.isError ? 'failed' : 'idle');
  };

  const correctionNotes = useMemo(
    () => (noteData?.notes || []).filter((note: OrderNote) => !!note.correctionType),
    [noteData],
  );

  // Extract unique user IDs from various order fields
  const userIds = useMemo(() => {
    const ids = new Set<string>();

    // Add user IDs from status history
    if (order.statusHistory) {
      order.statusHistory.forEach((historyItem) =>
        Object.values(historyItem).forEach((data) => {
          if (data?.updatedBy) {
            ids.add(data.updatedBy);
          }
        }),
      );
    }

    for (const payment of order.payments ?? []) {
      if (payment.createdByAdminUserId) {
        ids.add(payment.createdByAdminUserId);
      }
      if (payment.adminMasqueradeUserId) {
        ids.add(payment.adminMasqueradeUserId);
      }
    }

    // Add the order creator
    if (order.userId) {
      ids.add(order.userId);
    }

    // Add driver IDs if available
    if (order.dropOffDriverId) {
      ids.add(order.dropOffDriverId);
    }
    if (order.pickupDriverId) {
      ids.add(order.pickupDriverId);
    }

    // Correction authors are admins who may not appear anywhere else on the order.
    for (const note of correctionNotes) {
      if (note.createdBy) {
        ids.add(note.createdBy);
      }
    }

    return Array.from(ids);
  }, [order, correctionNotes]);

  // Fetch user data for all user IDs
  const { data: users = [], isLoading: isLoadingUsers } = trpc.admin.user.getUsersByIds.useQuery(
    userIds,
    {
      enabled: userIds.length > 0,
    },
  );

  // Create a map of user ID to user name
  const userNameMap = useMemo(() => {
    const map = new Map<string, string>();
    users.forEach((user) => {
      if (user && user._id) {
        const firstName = user.name?.first || '';
        const lastName = user.name?.last || '';
        const fullName = `${firstName} ${lastName}`.trim() || user.email || 'Unknown User';
        map.set(user._id.toString(), fullName);
      }
    });
    return map;
  }, [users]);

  // Generate activity items from order data
  const activityItems = useMemo((): ActivityItem[] => {
    const items: ActivityItem[] = [];

    // Order creation
    if (order.createdAt) {
      items.push({
        date: new Date(order.createdAt),
        description: 'Order Created',
        updatedBy: order.userId,
        details: `Order #${order.friendlyId} was created by customer`,
        type: 'system',
      });
    }

    // Status history activities
    if (order.statusHistory) {
      order.statusHistory.forEach((historyItem) => {
        Object.entries(historyItem).forEach(([status, data]) => {
          if (data) {
            let description = '';
            let details = '';
            let type: ActivityItem['type'] = 'status_change';

            switch (status as OrderStatusEnum) {
              case OrderStatusEnum.DropOffScheduled:
                description = 'Drop-off Scheduled';
                if (order.dropOffWindow) {
                  details = `Scheduled for ${formatCalendarDate(order.dropOffWindow.day, 'MMM d, yyyy')} between ${formatTimeWindow(order.dropOffWindow.start, order.dropOffWindow.end)}`;
                  if (order.dropOffWindow.immediateDropOffRequested) {
                    details += ' (Immediate delivery requested)';
                  }
                }
                type = 'scheduling';
                break;
              case OrderStatusEnum.PickupScheduled:
                description = 'Pickup Scheduled';
                if (order.pickupWindow) {
                  details = `Scheduled for ${formatCalendarDate(order.pickupWindow.day, 'MMM d, yyyy')} between ${formatTimeWindow(order.pickupWindow.start, order.pickupWindow.end)}`;
                  if (order.pickupWindow.isActualPickupDate) {
                    details += ' (Actual pickup date)';
                  }
                }
                type = 'scheduling';
                break;
              case OrderStatusEnum.PickupRequested:
                description = 'Pickup Requested';
                if (order.finalpickupWindow) {
                  details = `Customer requested pickup for ${formatCalendarDate(order.finalpickupWindow.day, 'MMM d, yyyy')} between ${formatTimeWindow(order.finalpickupWindow.start, order.finalpickupWindow.end)}`;
                }
                type = 'pickup';
                break;
              case OrderStatusEnum.DropOffConfirmed:
                description = 'Delivery Confirmed';
                if (order.deliveryConfirmedAt) {
                  details = `Containers delivered at ${formatDate(new Date(order.deliveryConfirmedAt))}`;
                }
                type = 'delivery';
                break;
              case OrderStatusEnum.PickupConfirmed:
                description = 'Pickup Confirmed';
                if (order.pickupConfirmedAt) {
                  details = `Containers picked up at ${formatDate(new Date(order.pickupConfirmedAt))}`;
                }
                type = 'pickup';
                break;
              case OrderStatusEnum.PaymentFailed:
                description = 'Payment Failed';
                details = 'Payment processing failed and requires manual intervention';
                type = 'card_payment';
                break;
              case OrderStatusEnum.Completed:
                description = 'Order Completed';
                details = 'Final payment processed and order completed';
                type = 'card_payment';
                break;
              case OrderStatusEnum.Refunded:
                description = 'Order Refunded';
                if (order.refunds && order.refunds.length > 0) {
                  const latestRefund = order.refunds[order.refunds.length - 1];
                  details = `Refund of $${(latestRefund.amountRefunded / 100).toFixed(2)} processed`;
                }
                type = 'refund';
                break;
              case OrderStatusEnum.RefundFailed:
                description = 'Refund Failed';
                details = 'Refund processing failed and requires manual intervention';
                type = 'refund';
                break;
              case OrderStatusEnum.Cancelled:
                description = 'Order Cancelled';
                details = 'Order was cancelled';
                type = 'system';
                break;
              case OrderStatusEnum.WasteProcessing:
                description = 'Waste Processing Started';
                details = 'Order moved to waste processing for final calculations';
                type = 'system';
                break;
              case OrderStatusEnum.Restricted:
                description = 'Order Flagged as Restricted';
                details = 'Order contains restricted materials and requires manual review';
                type = 'system';
                break;
              default:
                description = status.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
                type = 'status_change';
            }

            items.push({
              date: new Date(data.date),
              description,
              updatedBy: data.updatedBy,
              details,
              type,
            });
          }
        });
      });
    }

    // Container assignments
    if (order.containers && order.containers.length > 0) {
      order.containers.forEach((container) => {
        if (container.scheduledAt) {
          items.push({
            date: new Date(container.scheduledAt),
            description: `${container.action === 'drop-off' ? 'Drop-off' : 'Pickup'} Container Assigned`,
            details: container.window
              ? `Container(s) assigned for ${formatCalendarDate(container.window.day, 'MMM d, yyyy')} between ${formatTimeWindow(container.window.start, container.window.end)}`
              : 'Container(s) assigned; no scheduling date is recorded.',
            type: 'scheduling',
          });
        }
      });
    }
    // Payment activities
    if (order.payments && order.payments.length > 0) {
      order.payments.forEach((payment) => {
        const getPaymentInfo = (
          payment: OrderPayment,
        ): { description: string; details: string } => {
          if (payment.type === 'card') {
            return {
              description: 'Card Payment Processed',
              details: `Payment of $${(payment.amountPaid / 100).toFixed(2)} processed using card ending in ${payment.lastFour}`,
            };
          } else if (payment.type === 'non-card') {
            return {
              description: 'External Payment Processed',
              details: `Payment of $${(payment.amountPaid / 100).toFixed(2)} processed${payment.description ? ` - ${payment.description}` : ''}`,
            };
          } else if (payment.type === 'link') {
            return {
              description: 'Link Payment Processed',
              details: `Payment of $${(payment.amountPaid / 100).toFixed(2)} processed using link ending in ${payment.obfuscatedEmail}`,
            };
          } else if (payment.type === 'us_bank_account') {
            return {
              description: 'US Bank Account Payment Processed',
              details: `Payment of $${(payment.amountPaid / 100).toFixed(2)} processed using bank account ending in ${payment.lastFour}`,
            };
          }
          // this is just here for ts, it should never happen;
          return {
            description: 'Unknown Payment Type',
            details: `Payment processed`,
          };
        };

        // Determine actor attribution for payment activities:
        // - Masquerade payment: show customer name with admin badge (matches StatusHistory pattern)
        // - Direct admin payment: show admin name
        // - Customer payment: show customer name
        const isMasqueradePayment = !!payment.adminMasqueradeUserId;
        const updatedBy = isMasqueradePayment
          ? order.userId
          : payment.createdByAdminUserId || order.userId;

        items.push({
          date: new Date(payment.date),
          description: getPaymentInfo(payment).description,
          details: getPaymentInfo(payment).details,
          type: `${payment.type}_payment`,
          updatedBy,
          adminMasqueradeUserId: isMasqueradePayment ? payment.adminMasqueradeUserId : undefined,
        });
      });
    }

    // Refund activities
    if (order.refunds && order.refunds.length > 0) {
      order.refunds.forEach((refund) => {
        items.push({
          date: new Date(refund.date),
          description: 'Refund Processed',
          details: `Refund of $${(refund.amountRefunded / 100).toFixed(2)} processed`,
          type: 'refund',
        });
      });
    }

    // Field-level correction activities. Pushed into the same list as payments and refunds
    // rather than grouped on their own: dispute review depends on seeing what changed relative
    // to the payments and other actions around it.
    for (const note of correctionNotes) {
      const correctionType = note.correctionType as NoteCorrectionType;
      items.push({
        // `createdAt` is optional on the note model. NaN yields an Invalid Date, which the sort
        // below sinks to the bottom and `formatDate` renders as the missing-date placeholder,
        // rather than `new Date(undefined)` leaking a bogus timestamp into the audit trail.
        date: new Date(note.createdAt ?? NaN),
        description: correctionDescriptions[correctionType] ?? 'Order corrected',
        details: note.content,
        updatedBy: note.createdBy,
        type: 'correction',
      });
    }

    // Sort by date (most recent first). Items whose date is missing or unparseable
    // compare as NaN, which makes the sort non-deterministic — sink them to the bottom.
    return items.sort((a, b) => {
      const aTime = toValidDate(a.date)?.getTime();
      const bTime = toValidDate(b.date)?.getTime();
      if (aTime === undefined) return bTime === undefined ? 0 : 1;
      if (bTime === undefined) return -1;
      return bTime - aTime;
    });
  }, [order, correctionNotes]);

  const getActivityIcon = (type: ActivityItem['type']): string => {
    switch (type) {
      case 'status_change':
        return '🔄';
      case 'scheduling':
        return '📅';
      case 'card_payment':
        return '💳';
      case 'non_card_payment':
        return '💵';
      case 'link_payment':
        return '🔗';
      case 'us_bank_account_payment':
        return '🏦';
      case 'refund':
        return '💰';
      case 'delivery':
        return '🚚';
      case 'pickup':
        return '📦';
      case 'system':
        return '⚙️';
      // Corrections keep the default note/edit treatment - no new severity icon.
      case 'correction':
        return '📝';
      default:
        return '📝';
    }
  };

  const getActivityColor = (type: ActivityItem['type']): string => {
    switch (type) {
      case 'card_payment':
      case 'link_payment':
      case 'us_bank_account_payment':
      case 'non-card_payment':
      case 'delivery':
        return 'bg-green-100 border-green-300';
      case 'refund':
      case 'pickup':
        return 'bg-blue-100 border-blue-300';
      case 'scheduling':
        return 'bg-purple-100 border-purple-300';
      case 'system':
        return 'bg-gray-100 border-gray-300';
      // Neutral slate so corrections read as reference records, distinguishable from the green
      // payment and blue refund cards without looking like warnings.
      case 'correction':
        return 'bg-slate-100 border-slate-300';
      default:
        return 'bg-slate-100 border-slate-300';
    }
  };

  // Computed after filtering, not before: an order whose only Activity Log records are
  // corrections must show them rather than fall through to the empty state.
  const visibleItems = activityItems.filter((item) => displayedTypes.includes(item.type));

  // Sits above the timeline rather than replacing it. Payments and refunds come off the order
  // document and are unaffected by a failed notes query, so they stay on screen - the notice
  // marks what is missing instead of hiding what is known.
  const correctionHistoryNotice =
    correctionHistory === 'loading' ? (
      <div
        className="rounded-lg border border-slate-300 bg-slate-50 p-4"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <div className="flex items-start gap-3">
          <LoaderCircle
            aria-hidden="true"
            className="mt-0.5 h-4 w-4 flex-shrink-0 animate-spin text-slate-500"
          />
          <p className="text-sm text-slate-700 break-words">
            Loading correction history. This timeline is incomplete until it finishes.
          </p>
        </div>
      </div>
    ) : isCorrectionHistoryFailed ? (
      <div
        className="rounded-lg border border-destructive/30 bg-destructive/5 p-4"
        role="alert"
        aria-labelledby={CORRECTION_HISTORY_TITLE_ID}
        aria-describedby={CORRECTION_HISTORY_BODY_ID}
        aria-busy={isRetryingNotes || undefined}
      >
        <div className="flex items-start gap-3">
          <AlertTriangle
            aria-hidden="true"
            className="mt-0.5 h-4 w-4 flex-shrink-0 text-destructive"
          />
          <div className="flex-1 min-w-0">
            <h4
              id={CORRECTION_HISTORY_TITLE_ID}
              className="text-sm font-medium text-destructive break-words"
            >
              {correctionHistory === 'stale'
                ? 'Correction history may be out of date'
                : 'Correction history couldn’t load'}
            </h4>
            {/* The stale copy has to be explicit that entries are still on screen. Reusing the
                "changes are missing" wording would contradict the visible correction cards and
                leave an admin unsure which half to trust. */}
            <p id={CORRECTION_HISTORY_BODY_ID} className="mt-1 text-sm text-slate-700 break-words">
              {correctionHistory === 'stale'
                ? 'The corrections below are from an earlier load and newer customer or address changes may be missing, so this timeline may be incomplete. Check your connection and try again.'
                : 'Customer and address changes are missing from this timeline, so it is incomplete. Check your connection and try again.'}
            </p>
            <Button
              className="mt-3 min-h-11 w-full sm:w-auto"
              variant="outline"
              aria-disabled={isRetryingNotes || undefined}
              onClick={() => void handleRetryNotes()}
            >
              {isRetryingNotes && (
                <LoaderCircle aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />
              )}
              {isRetryingNotes ? 'Trying again…' : 'Try again'}
            </Button>
          </div>
        </div>
      </div>
    ) : null;

  // Announces the retry outcome without minting a second `role="alert"`, which would otherwise
  // re-interrupt a screen reader on every attempt.
  const correctionHistoryRetryStatus = isCorrectionHistoryFailed && (
    <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">
      {isRetryingNotes
        ? 'Trying to load correction history.'
        : notesRetryState === 'failed'
          ? 'Correction history still couldn’t load. Try again.'
          : ''}
    </p>
  );

  // The definitive empty state is gated on a successful notes load. Reaching it while the
  // correction history is pending or failed would assert an empty audit trail nobody verified.
  // `stale` is excluded for the same reason even though it has data: an empty cached payload that
  // failed to refresh cannot rule out corrections recorded since it was cached.
  if (visibleItems.length === 0 && correctionHistory === 'loaded') {
    return (
      <div className="px-2">
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <p className="text-slate-500 text-sm">No activity log available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-2">
      <div className="flex flex-col gap-4">
        {correctionHistoryNotice}
        {correctionHistoryRetryStatus}
        {visibleItems.map((item, index) => (
          <div
            key={`${item.description}-${index}`}
            className={`rounded-lg border p-4 ${getActivityColor(item.type)}`}
          >
            <div className="flex items-start gap-3">
              {/* flex-shrink-0 so the glyph keeps its own box in a phone-width drawer instead
                  of being compressed by a long wrapping title beside it. aria-hidden because
                  the glyph only restates the title beside it - without it a screen reader
                  announces "memo"/"credit card"/"money bag" before every entry. */}
              <div className="text-lg flex-shrink-0" aria-hidden="true">
                {getActivityIcon(item.type)}
              </div>
              <div className="flex-1 min-w-0">
                {/* flex-wrap, so a narrow drawer drops the timestamp onto its own line
                      instead of squeezing the title. Change text is never truncated. */}
                <div className="flex flex-wrap justify-between items-start gap-x-2 gap-y-1 mb-1">
                  <h4 className="font-medium text-sm text-slate-900 break-words">
                    {item.description}
                  </h4>
                  <span className="text-xs text-slate-600 whitespace-nowrap">
                    {formatDate(item.date)}
                  </span>
                </div>

                {item.details && (
                  <p className="text-xs text-slate-700 mb-2 break-words">{item.details}</p>
                )}

                {item.updatedBy && (
                  <div className="flex min-w-0 items-center gap-2">
                    <p
                      className={`min-w-0 [overflow-wrap:anywhere] text-xs ${
                        item.type === 'correction' ? 'text-slate-600' : 'text-slate-500'
                      }`}
                    >
                      {isLoadingUsers
                        ? 'Loading...'
                        : `By: ${userNameMap.get(item.updatedBy) || 'Unknown User'}`}
                    </p>
                    {!!item.adminMasqueradeUserId && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-200">
                        <Key className="w-3 h-3 mr-1" />
                        Admin:{' '}
                        {isLoadingUsers
                          ? 'Loading...'
                          : `${userNameMap.get(item.adminMasqueradeUserId) || 'Unknown Admin'}`}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
